import * as turf from "@turf/turf";
import { formatArea } from "./formatting";
import triangle from "../assets/img/triangle.png";
import mapPin from "../assets/img/map-pin-png.png";
import mapOverlay from "../assets/img/map-overlay.png";

export const isMapExist = (map) => {
  if (!map) return false;

  try {
    return !!map.getStyle();
  } catch {
    return false;
  }
};

export const mapOff = (map, ...args) => {
  if (!isMapExist(map)) return;
  try {
    map.off(...args);
  } catch {}
};

/**
 * Adds a new source if it does not exist, or updates the GeoJSON data if it does.
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} sourceId - A unique ID for the source.
 * @param {object} geoJSON - A valid GeoJSON object (Feature or FeatureCollection).
 */
export function addOrUpdateSource(map, sourceId, geoJSON) {
  if (!isMapExist(map)) return;

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: "geojson",
      data: geoJSON,
    });
  } else {
    map.getSource(sourceId).setData(geoJSON);
  }
}

/**
 * Adds a new layer if it does not already exist.
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} layerConfig - Configuration object for the layer (must have an id and source).
 */
export function addLayerIfNotExists(map, layerConfig, layerBeforeID = null) {
  if (!isMapExist(map)) return;

  const { id } = layerConfig;
  if (!map.getLayer(id)) {
    map.addLayer(layerConfig, layerBeforeID);
  }
}

/**
 * Removes a layer and its source if they exist on the map.
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} layerId - The ID of the layer to remove.
 * @param {string} sourceId - The ID of the source to remove.
 */
export function removeLayerAndSource(map, layerId, sourceId) {
  if (!isMapExist(map)) return;

  try {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  } catch (error) {
    console.warn(`Failed to remove layer "${layerId}"`, error);
  }

  try {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  } catch (error) {
    console.warn(`Failed to remove source "${sourceId}"`, error);
  }
}

/**
 * Adds a clipping layer to the map using the provided property feature.
 * This layer (of type "clip") defines a clipping region for basemap symbols and models.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} propertyFeature - A GeoJSON Feature representing the property polygon.
 */
export function addClipLayer(map, propertyFeature, idPrefix = "main") {
  addOrUpdateSource(map, "eraser-source", propertyFeature);

  addLayerIfNotExists(map, {
    id: "eraser-layer",
    type: "clip",
    source: "eraser-source",
    layout: {
      "clip-layer-types": ["symbol", "model"],
      "clip-layer-scope": ["basemap"],
    },
  });
}

/**
 * Removes the clipping layer ("eraser-layer") and its associated source ("eraser-source") from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeClipLayer(map) {
  removeLayerAndSource(map, "eraser-layer", "eraser-source");
}

/**
 * Adds a property layer to the map using the provided property feature.
 * The property polygon is rendered with a white fill and a hatched-line pattern.
 * This function also loads the hatched-line image if it is not already available.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} propertyFeature - A GeoJSON Feature representing the property.
 */
export function addPropertyLayer(map, propertyFeature, idPrefix = "main") {
  if (!isMapExist(map)) return;

  // Load and add the hatched-line image if not already available.
  const img = new Image();
  img.src =
    "data:image/svg+xml;base64,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";
  img.onload = function () {
    if (!map.hasImage("hatched-line-img")) {
      map.addImage("hatched-line-img", img);
    }
  };

  const sourceId = `property-source-${idPrefix}`;
  const layerId = `property-layer-${idPrefix}`;

  // Add the property polygon.
  addOrUpdateSource(map, sourceId, propertyFeature);
  addLayerIfNotExists(map, {
    id: layerId,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": "#fff",
      "fill-opacity": 1,
      "fill-pattern": "hatched-line-img",
    },
  });
}

/**
 * Removes the property layer and its associated source from the map.
 * Also removes the hatched-line image if it exists.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removePropertyLayer(map, idPrefix = "main") {

  removeLayerAndSource(
    map,
    `property-layer-${idPrefix}`,
    `property-source-${idPrefix}`
  );
  if (map.hasImage("hatched-line-img")) {
    map.removeImage("hatched-line-img");
  }
}

/**
 * Adds an inner property layer to the map using the provided offset polygon.
 * This layer displays an inner zone with a distinct fill color.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} offsetPolygon - A GeoJSON Feature representing the offset polygon.
 */
export function addInnerPropertyLayer(map, offsetPolygon, idPrefix = "main") {
  const sourceId = `inner-property-source-${idPrefix}`;
  const layerId = `inner-property-layer-${idPrefix}`;

  addOrUpdateSource(map, sourceId, offsetPolygon);
  addLayerIfNotExists(map, {
    id: layerId,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": "#5E6278",
      "fill-opacity": 1,
    },
  });
}

/**
 * Removes the inner property layer and its associated source from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeInnerPropertyLayer(map, idPrefix = "main") {
  removeLayerAndSource(
    map,
    `inner-property-layer-${idPrefix}`,
    `inner-property-source-${idPrefix}`
  );
}

/**
 * Adds a property edges layer to the map using the provided edges GeoJSON data.
 * This function renders building edges as lines with a color that depends on whether
 * an edge matches the selected line. It also adds an entry-side symbol layer (using a triangle image)
 * along the selected line.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} edgesGeoData - A GeoJSON object representing the property edges.
 * @param {Object} selectedLine - An object representing the selected line, whose properties are used for styling.
 */
export function addPropertyEdgesLayer(
  map,
  edgesGeoData,
  selectedLine,
  idPrefix = "main"
) {
  addOrUpdateSource(map, `property-edges-source-${idPrefix}`, edgesGeoData);
  addLayerIfNotExists(map, {
    id: `property-edges-layer-${idPrefix}`,
    type: "line",
    source: `property-edges-source-${idPrefix}`,
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": [
        "case",
        ["==", ["get", "id"], selectedLine.properties.id],
        "#efa21e",
        "#5E6278",
      ] /* "#5E6278", */, // not selected
      "line-width": 3,
      "line-opacity": 1,
    },
  });

  addOrUpdateSource(
    map,
    `property-entry-side-source-${idPrefix}`,
    selectedLine
  );

  map.loadImage(triangle, (error, image) => {
    if (error) throw error;
    if (!map.hasImage("triangle-img")) {
      map.addImage("triangle-img", image);
    }

    addLayerIfNotExists(map, {
      id: `property-entry-side-layer-${idPrefix}`,
      type: "symbol",
      source: `property-entry-side-source-${idPrefix}`,
      layout: {
        // Tells Mapbox to place symbols along the line geometry
        "symbol-placement": "line",
        // Spacing between repeated icons (in pixels or *icon-size* context).
        // If you want it in meters, see note below.
        "symbol-spacing": 50,
        "icon-anchor": "top",
        "icon-size": 0.2,
        "icon-rotate": 180,
        "icon-image": "triangle-img",
        "icon-allow-overlap": false,

        // Align the icon to the direction of the line on the map
        "icon-rotation-alignment": "map",

        // If you want them to face "forward" or "reverse" the line direction,
        // you can try 'icon-rotate': 180 or so, or other experiments
        // 'icon-rotate': 180,
      },
    });
  });
}

/**
 * Removes the property edges layer and the entry-side symbol layer (and their sources)
 * from the map. Also removes the triangle image if it exists.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removePropertyEdgesLayer(map, idPrefix = "main") {
  removeLayerAndSource(
    map,
    `property-edges-layer-${idPrefix}`,
    `property-edges-source-${idPrefix}`
  );
  removeLayerAndSource(
    map,
    `property-entry-side-layer-${idPrefix}`,
    `property-entry-side-source-${idPrefix}`
  );
  if (map.hasImage("triangle-img")) {
    map.removeImage("triangle-img");
  }
}

/**
 * Adds or updates the property vertex point layer on the map using the provided GeoJSON data.
 * This layer displays vertex points for the property polygon.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} vertexGeoData - A GeoJSON object containing vertex label features.
 */
export function addPropertyVertexPointLayer(map, vertexGeoData) {
  const sourceId = "property-vertex-point-source";
  addOrUpdateSource(map, "property-vertex-point-source", vertexGeoData);
  addLayerIfNotExists(map, {
    id: "property-vertex-point-layer",
    type: "circle",
    source: "property-vertex-point-source",
    paint: {
      "circle-radius": 4,
      "circle-color": "#5E6278",
      "circle-stroke-width": 1,
      "circle-stroke-color": "#fff",
    },
  });
}

/**
 * Removes the property vertex point layer from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removePropertyVertexPointLayer(map) {
  removeLayerAndSource(
    map,
    "property-vertex-point-layer",
    "property-vertex-point-source"
  );
}

/**
 * Adds or updates the property distance labels layer on the map using the provided GeoJSON data.
 * This layer displays distance measurements as text labels.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} distanceGeoData - A GeoJSON object containing distance label features.
 */
export function addPropertyDistanceLabels(map, distanceGeoData) {
  addOrUpdateSource(map, "property-distance-source", distanceGeoData);
  addLayerIfNotExists(map, {
    id: "property-distance-layer",
    type: "symbol",
    source: "property-distance-source",
    layout: {
      "text-field": ["get", "distance"],
      "text-size": 14,
      "text-anchor": "top",
      "text-offset": [0, 0.5],
      "text-letter-spacing": 0.1,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 1,
    },
  });
}

/**
 * Adds or updates the property vertex labels layer on the map using the provided GeoJSON data.
 * This layer displays vertex labels for the property polygon.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} vertexGeoData - A GeoJSON object containing vertex label features.
 */
export function addPropertyVertexLabels(map, vertexGeoData) {
  const sourceId = "property-vertex-source";
  addOrUpdateSource(map, "property-vertex-source", vertexGeoData);
  addLayerIfNotExists(map, {
    id: "property-vertex-layer",
    type: "symbol",
    source: "property-vertex-source",
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,
      "text-offset": [0, -1],
      "text-anchor": "bottom",
      "text-letter-spacing": 0.1,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 1,
    },
  });
}

/**
 * Adds or updates the property area labels layer on the map using the provided GeoJSON data.
 * This layer displays area labels for the property polygon.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} areaGeoData - A GeoJSON object containing area label features.
 */
export function addPropertyAreaLabel(map, areaGeoData) {
  addOrUpdateSource(map, "property-area-source", areaGeoData);
  addLayerIfNotExists(map, {
    id: "property-area-layer",
    type: "symbol",
    source: "property-area-source",
    layout: {
      "text-field": ["get", "area"],
      "text-size": 14,
      "text-anchor": "center",
      "text-letter-spacing": 0.1,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 1,
    },
  });
}

/**
 * Removes all property label layers (distance, vertex, and area) along with their sources from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeAllPropertyLabels(map) {
  removeLayerAndSource(
    map,
    "property-distance-layer",
    "property-distance-source"
  );
  removeLayerAndSource(map, "property-vertex-layer", "property-vertex-source");
  removeLayerAndSource(map, "property-area-layer", "property-area-source");
}

/**
 * Adds both the buildings layer and the areas layer to the map.
 * The buildings layer is rendered as a fill-extrusion using the provided buildings GeoJSON data,
 * and the areas layer is rendered as a fill using the provided areas GeoJSON data.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} buildingsGeoData - A GeoJSON object representing building features.
 * @param {Object} areaGeoData - A GeoJSON object representing area features.
 */
export function addBuildingsAndAreasLayer(map, buildingsGeoData, areaGeoData) {
  addOrUpdateSource(map, "buildings-source", buildingsGeoData);
  addLayerIfNotExists(
    map,
    {
      id: "buildings-layer",
      type: "fill-extrusion",
      source: "buildings-source",
      layout: {
        "fill-extrusion-edge-radius": 0.3,
        /* "fill-extrusion-rounded-roof": true, */
      },
      paint: {
        "fill-extrusion-color": ["get", "color"],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": 0,
        "fill-extrusion-opacity": 1,
        "fill-extrusion-vertical-gradient": false,
      },
    }
    /* "property-polygon-layer" */
  );

  addOrUpdateSource(map, "areas-source", areaGeoData);
  addLayerIfNotExists(map, {
    id: "areas-layer",
    type: "fill",
    source: "areas-source",
    paint: {
      "fill-color": ["get", "color"],
      "fill-opacity": 1,
    },
  });
}

/**
 * Removes both the buildings layer and the areas layer, along with their associated sources, from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeBuildingsAndAreasLayer(map) {
  removeLayerAndSource(map, "buildings-layer", "buildings-source");
  removeLayerAndSource(map, "areas-layer", "areas-source");
}

/**
 * Adds both the docking bays layer to the map.
 * The docking bays layer is rendered as a fill-extrusion using the provided docking bays GeoJSON data
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} dockingBaysGeoData - A GeoJSON object representing the docking bays features.
 */
export function addDockingBaysLayer(map, dockingBaysGeoData) {
  addOrUpdateSource(map, "docking-bays-source", dockingBaysGeoData);
  addLayerIfNotExists(
    map,
    {
      id: "docking-bays-layer",
      type: "fill-extrusion",
      source: "docking-bays-source",
      layout: {
        /* "fill-extrusion-edge-radius": 0.3, */
        /* "fill-extrusion-rounded-roof": true, */
      },
      paint: {
        "fill-extrusion-color": ["get", "color"],
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": 0,
        "fill-extrusion-opacity": 1,
        "fill-extrusion-vertical-gradient": false,
      },
    }
    /* "property-polygon-layer" */
  );
}

/**
 * Removes the docking bays layer, along with their associated sources, from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeDockingBaysLayer(map) {
  removeLayerAndSource(map, "docking-bays-layer", "docking-bays-source");
}

/**
 * Adds a routes layer to the map if it does not already exist.
 * The layer displays route data using a fill style.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} routesGeoData - A GeoJSON object representing the routes.
 */
export function addRoutesLayer(map, id, routesGeoData) {
  addOrUpdateSource(map, `${id}-source`, routesGeoData);
  addLayerIfNotExists(map, {
    id: `${id}-layer`,
    type: "fill",
    source: `${id}-source`,
    paint: {
      "fill-color": ["get", "color"],
      "fill-opacity": 1,
    },
  });
}

/**
 * Removes the routes layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeRoutesLayer(map, id) {
  removeLayerAndSource(map, `${id}-layer`, `${id}-source`);
}

/**
 * Adds a routes middel line layer to the map if it does not already exist.
 * The layer displays route middel line data using a fill style.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} routesGeoData - A GeoJSON object representing the routes.
 */
export function addRoutesMiddleLineLayer(map, id, routesGeoData) {
  addOrUpdateSource(map, `${id}-source`, routesGeoData);
  addLayerIfNotExists(
    map,
    {
      id: `${id}-layer`,
      type: "line",
      source: `${id}-source`,
      paint: {
        "line-color": "#fff",
        "line-dasharray": [5, 10],
      },
    } /* "routes-layer" */
  );
}

/**
 * Removes the routes middel line layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeRoutesMiddleLineLayer(map, id) {
  removeLayerAndSource(map, `${id}-layer`, `${id}-source`);
}

/**
 * Adds the parking slots layer to the map using the provided data.
 * This layer displays an parking slots white lines.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} offsetPolygon - A GeoJSON Feature representing the offset polygon.
 */
export function addParkingSlotsLayer(map, parkingSlots) {
  addOrUpdateSource(map, "parking-slots-source", parkingSlots);
  addLayerIfNotExists(
    map,
    {
      id: "parking-slots-layer",
      type: "line",
      source: "parking-slots-source",
      paint: {
        "line-color": "#d0d5dd",
        /* "fill-opacity": 1, */
      },
    } /* "buildings-layer" */
  );
}

/**
 * Removes the parking slots layer and its associated source from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeParkingSlotsLayer(map) {
  removeLayerAndSource(map, "parking-slots-layer", "parking-slots-source");
}

/**
 * Adds a building names label layer to the map if it does not already exist.
 * This layer displays labels for building names using the provided GeoJSON data.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} buildingsNameGeoData - A GeoJSON object containing building name features.
 */
export function addBuildingsNameLabels(map, buildingsNameGeoData) {
  addOrUpdateSource(map, "buildings-name-source", buildingsNameGeoData);
  addLayerIfNotExists(map, {
    id: "buildings-name-layer",
    type: "symbol",
    source: "buildings-name-source",
    layout: {
      "symbol-placement": "point",
      "symbol-z-order": "auto",
      "symbol-z-elevate": true,
      "text-field": ["get", "name"],
      "text-size": 12,
      "text-anchor": "top",
      /* "text-offset": [0, 0.5], */
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#ffffff",
      /* "text-halo-width": 0.1, */
      /* "text-halo-blur": 0, */
      // Add background color and padding
      /* "text-background-color": "#ffffff",
      "text-background-padding": [2, 2], */
    },
  });
}

/**
 * Removes the building names label layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeBuildingsNameLabels(map) {
  removeLayerAndSource(map, "buildings-name-layer", "buildings-name-source");
}

/**
 * Adds a building edges layer to the map if it does not already exist.
 * The layer displays the edges of a building polygon using a line style.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} buildingEdgesGeoData - A GeoJSON object representing the building edges.
 * @param {number} height - A height offset used for rendering the edges.
 */
export function addBuildingEdgesLayer(map, buildingEdgesGeoData, height) {
  addOrUpdateSource(map, "building-edges-source", buildingEdgesGeoData);
  addLayerIfNotExists(map, {
    id: "building-edges-layer",
    type: "line",
    source: "building-edges-source",
    layout: { "line-z-offset": height || 0 },
    paint: {
      "line-color": "#efa21e" /* "#5E6278", */, // not selected
      "line-width": 3,
      "line-opacity": 1,
    },
  });
}

/**
 * Removes the building edges layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeBuildingEdgesLayer(map) {
  removeLayerAndSource(map, "building-edges-layer", "building-edges-source");
}

/**
 * Adds a layer for the axis grid to the map if it does not already exist.
 * The layer displays the axis grid of a building using a line style.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} buildingAxisGridGeoData - A GeoJSON object representing the building edges.
 * @param {number} height - A height offset used for rendering the edges.
 */
export function addBuildingAxisGridLayer(map, buildingAxisGridGeoData, height) {
  addOrUpdateSource(map, "building-axis-grid-source", buildingAxisGridGeoData);
  addLayerIfNotExists(map, {
    id: "building-axis-grid-layer",
    type: "line",
    source: "building-axis-grid-source",
    layout: { "line-z-offset": height || 0 },
    paint: {
      "line-color": "#efa21e" /* "#5E6278", */, // not selected
      "line-width": 2,
      "line-opacity": 0.8,
      "line-dasharray": [4, 1.5, 0.5, 1],
    },
  });
}

/**
 * Removes the building axis grid layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeBuildingAxisGridLayer(map) {
  removeLayerAndSource(
    map,
    "building-axis-grid-layer",
    "building-axis-grid-source"
  );
}

/**
 * Adds or updates the building distance labels layer on the map.
 * The layer displays distance values based on the provided GeoJSON data.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {object} distanceGeoData - A GeoJSON object representing the distance label features.
 */
export function addBuildingDistanceLabels(map, distanceGeoData) {
  addOrUpdateSource(map, "building-distance-source", distanceGeoData);
  addLayerIfNotExists(map, {
    id: "building-distance-layer",
    type: "symbol",
    source: "building-distance-source",
    layout: {
      "text-field": ["get", "distance"],
      "text-size": 12,
      "text-anchor": "top",
      "text-offset": [0, 0.5],
      "symbol-placement": "point",
      "symbol-z-order": "auto",
      "symbol-z-elevate": true,
      "text-allow-overlap": true,
      "text-letter-spacing": 0.1,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 0.8,
    },
  });
}

/**
 * Removes the building distance labels layer and its associated source from the map, if they exist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeBuildingDistanceLabels(map) {
  removeLayerAndSource(
    map,
    "building-distance-layer",
    "building-distance-source"
  );
}

/**
 * Loads an image into the Mapbox style and registers it under the given imageId.
 * If the image is already registered on the map (map.hasImage), it does nothing.
 *
 * Use this to safely reuse a single image across multiple layers without duplicating work.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} imageUrl - URL (or imported asset URL) of the image to load.
 * @param {string} imageId - Unique image identifier used by Mapbox style layers (icon-image).
 * @returns {Promise<string>} Resolves with the imageId once the image is available on the map.
 */
export function addMapImage(map, imageUrl, imageId) {
  return new Promise((resolve, reject) => {
    if (map.hasImage(imageId)) return resolve(imageId);

    map.loadImage(imageUrl, (err, img) => {
      if (err) return reject(err);
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, img);
      }
      resolve(imageId);
    });
  });
}

/**
 * Adds or updates a "property center marker" as a symbol layer.
 * The marker is represented by a pin icon plus a label (address/title) drawn from feature properties.
 *
 * The layer and source ids are namespaced by idPrefix so multiple properties can be displayed
 * at the same time (e.g. "main", "extra-1", etc.).
 *
 * The marker is configured to display only when zoomed out (via maxzoom).
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {[number, number]} coords - Marker coordinates in [lng, lat] format.
 * @param {string} [location='title'] - Text label to display next to the marker (e.g. address or name).
 * @param {string} [idPrefix='main'] - Prefix used to generate unique layer/source ids for this property.
 * @returns {Promise<void>} Resolves when the image is loaded and the marker layer is added.
 */
export async function addPropertyCenterMarkerLayer(
  map,
  coords,
  location = 'title',
  idPrefix = "main"
) {
  const sourceId = `property-center-source-${idPrefix}`;
  const layerId = `property-center-layer-${idPrefix}`;
  const imageId = "property-pin-img";

  const fc = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: coords },
        properties: {
           title: location,
        },
      },
    ],
  };

  addOrUpdateSource(map, sourceId, fc);

  await addMapImage(map, mapPin, imageId);

  addLayerIfNotExists(map, {
    id: layerId,
    type: "symbol",
    source: sourceId,

    // show only when zoomed out
    maxzoom: 14,

    layout: {
      "icon-image": imageId,
      "icon-size": 1.2, 
      "icon-anchor": "bottom", 
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      'text-field': ['get', 'title'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-offset': [0, 0.5],
      'text-anchor': 'top',
      'text-size': 14,
    },
  });
}

/**
 * Removes the property center marker layer and its associated source from the map.
 *
 * Note: This function does NOT remove the shared pin image from the map style, because the same
 * image may be reused by other marker layers. Image cleanup should be done separately if needed.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} [idPrefix='main'] - Prefix used to generate unique layer/source ids for this property.
 */
export function removePropertyCenterMarkerLayer(map, idPrefix = "main") {
  removeLayerAndSource(
    map,
    `property-center-layer-${idPrefix}`,
    `property-center-source-${idPrefix}`
  );
  if (map.hasImage("property-pin-img")) {
    map.removeImage("property-pin-img");
  }
}

/**
 * Adds or updates a directions route layer on the map from a provided GeoJSON Feature.
 * This is typically used to visualize a route geometry returned by a routing engine (e.g. Mapbox Directions API).
 *
 * The route is stored in a dedicated GeoJSON source and rendered as a styled line.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {Object} routeFeature - A GeoJSON Feature (usually a LineString) representing the route geometry.
 */
export function addOrUpdateDirectionRouteLayer(map, routeFeature, idPrefix) {
  const sourceId = `direction-route-source-${idPrefix}`;
  const layerId = `direction-route-layer-${idPrefix}`;

  addOrUpdateSource(map, sourceId, {
    type: "FeatureCollection",
    features: [routeFeature],
  });

  addLayerIfNotExists(map, {
    id: layerId,
    type: "line",
    source: sourceId,
    paint: {
      "line-width": 7,
      "line-opacity": 1,
      'line-color': '#efa21e'
    },
  });
}

/**
 * Removes the directions route layer and its associated source from the map.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 */
export function removeDirectionRouteLayer(map, idPrefix) {
  removeLayerAndSource(
    map,
    `direction-route-layer-${idPrefix}`,
    `direction-route-source-${idPrefix}`
  );
}

/**
 * Adds or updates an image overlay on top of the basemap tiles using an "image" source + "raster" layer.
 * The image is georeferenced by 4 corner coordinates and will move/rotate with the map.
 *
 * Notes:
 * - This uses a Mapbox "image" source (single image warped to 4 corners).
 * - Coordinates order must be:
 *   [top-left, top-right, bottom-right, bottom-left]
 * - Layer/source ids are namespaced by idPrefix so multiple overlays can coexist.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} imageUrl - URL (or imported asset URL) for the overlay image (PNG/JPG).
 * @param {Array<[number, number]>} coordinates - 4 corners in [lng, lat] order:
 *   [[tlLng, tlLat], [trLng, trLat], [brLng, brLat], [blLng, blLat]]
 * @param {string} [idPrefix='main'] - Prefix used to generate unique layer/source ids.
 * @param {Object} [options={}] - Optional configuration.
 * @param {number} [options.opacity=0.8] - Raster opacity (0..1).
 * @param {number} [options.minzoom] - Minimum zoom to show the overlay layer.
 * @param {number} [options.maxzoom] - Maximum zoom to show the overlay layer.
 * @param {string} [options.beforeId] - Insert the layer before this existing layer id (if provided).
 */
export function addOrUpdateImageOverlayLayer(
  map,
  imageUrl,
  coordinates,
  idPrefix = "main",
  options = {}
) {
  if (!isMapExist(map)) return;
  
  const {
    opacity = 1,
    minzoom,
    maxzoom,
    beforeId,
  } = options;

  const sourceId = `image-overlay-source-${idPrefix}`;
  const layerId = `image-overlay-layer-${idPrefix}`;

  // --- Add or update the "image" source ---
  const existingSource = map.getSource(sourceId);

  if (!existingSource) {
    map.addSource(sourceId, {
      type: "image",
      url: mapOverlay,
      coordinates,
    });
  } else {
    if (typeof existingSource.updateImage === "function") {
      existingSource.updateImage({ url: mapOverlay, coordinates });
    } else {
      if (typeof existingSource.setCoordinates === "function") {
        existingSource.setCoordinates(coordinates);
      }
    }
  }

  // If you need insertion order, your addLayerIfNotExists should ideally accept `beforeId`.
  const layerDef = {
    id: layerId,
    type: "raster",
    source: sourceId,
    paint: {
      "raster-opacity": opacity,
    },
    ...(typeof minzoom === "number" ? { minzoom } : {}),
    ...(typeof maxzoom === "number" ? { maxzoom } : {}),
  };

  if (beforeId) {
    addLayerIfNotExists(map, layerDef, beforeId);
  } else {
    addLayerIfNotExists(map, layerDef);
  }
}

/**
 * Removes an image overlay layer and its associated source from the map.
 * Matches ids used by addOrUpdateImageOverlayLayer.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance.
 * @param {string} [idPrefix='main'] - Prefix used to generate unique layer/source ids.
 */
export function removeImageOverlayLayer(map, idPrefix = "main") {
  removeLayerAndSource(
    map,
    `image-overlay-layer-${idPrefix}`,
    `image-overlay-source-${idPrefix}`
  );
}
